import React, { useState, useEffect, useCallback } from "react";
import { message, Modal } from "antd";
import Layout from "./common/Layout";
import Loading from "./common/Loading";
import Pagination from "./common/Pagination";
import { get_messages_list } from "../apis/GetMessagesList";
import { get_message_detail } from "../apis/GetMessageDetail";
import { mark_message_read } from "../apis/MarkMessageRead";
import { mark_all_read } from "../apis/MarkAllRead";
import { delete_message } from "../apis/DeleteMessage";
import { get_unread_count } from "../apis/GetUnreadCount";
import "../css/messageCenter.scss";
import { setAPIAuthorizationToken } from "../apis/API";
import Cookies from "js-cookie";
import { add_or_update_message } from "../apis/AddOrUpdateMessage";
import { get_user } from "../apis/GetUser";

const MessageCenter = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    type: "all", // all, system, account, transaction, announcement
    is_read: "all", // all, 0, 1
    title: "",
  });
  // 是否具备管理员能力：通过能否获取到用户列表来判断（与其他页面一致）
  const [users, setUsers] = useState([]);
  // 编辑器状态
  const [showEditor, setShowEditor] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null); // null=新建
  const [editorForm, setEditorForm] = useState({
    user_id: "",
    type: "system",
    title: "",
    content: "",
    priority: 0,
  });

  // 初始化token
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      setAPIAuthorizationToken(token);
    }
    // 试图加载用户列表，成功则视为管理员
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await get_user();
      if (res && res.data && res.data.code === 200) {
        const userData = res.data.data;
        if (userData && userData.List && Array.isArray(userData.List)) {
          setUsers(userData.List);
        } else {
          setUsers([]);
        }
      }
    } catch (e) {
      setUsers([]);
    }
  };

  // 获取消息列表
  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        ...filters,
        page: currentPage,
        page_size: pageSize,
      };

      const response = await get_messages_list(params);

      if (response.data && response.data.code === 200) {
        const data = response.data.data;
        // 对消息列表进行排序：未读消息(is_read=0)排在最前面
        const sortedMessages = (data.list || []).sort((a, b) => {
          // 首先按未读状态排序 (未读=0 排在前面)
          if (a.is_read !== b.is_read) {
            return a.is_read - b.is_read;
          }
          // 如果未读状态相同，则按创建时间降序排序 (新消息在前)
          return new Date(b.created_at) - new Date(a.created_at);
        });

        setMessages(sortedMessages);
        setUnreadCount(data.unread_count || 0);

        if (data.pagination) {
          setTotal(data.pagination.total || 0);
          setCurrentPage(data.pagination.page || 1);
          setPageSize(data.pagination.page_size || pageSize);
        }
      } else {
        setError(response.data?.message || "获取消息列表失败");
      }
    } catch (err) {
      console.error("获取消息列表失败:", err);
      setError(err.response?.data?.message || "获取消息列表失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filters]);

  // 获取未读消息数量
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await get_unread_count();
      if (response.data && response.data.code === 200) {
        setUnreadCount(response.data.data.total_unread || 0);
      }
    } catch (err) {
      console.error("获取未读数量失败:", err);
    }
  }, []);

  // 组件加载时获取数据
  useEffect(() => {
    fetchMessages();
    fetchUnreadCount();
  }, [fetchMessages, fetchUnreadCount]);

  // 打开新建
  const openCreate = () => {
    setEditingMessage(null);
    setEditorForm({
      user_id: "",
      type: "system",
      title: "",
      content: "",
      priority: 0,
    });
    setShowEditor(true);
  };

  // 打开编辑
  const openEdit = (msg) => {
    setEditingMessage(msg);
    setEditorForm({
      user_id: msg.user_id || "",
      type: msg.type || "system",
      title: msg.title || "",
      content: msg.content || "",
      priority:
        typeof msg.priority === "number"
          ? msg.priority
          : Number(msg.priority || 0),
    });
    setShowEditor(true);
  };

  // 保存
  const handleSaveMessage = async () => {
    // 简单校验
    if (!editorForm.user_id || !editorForm.title || !editorForm.content) {
      message.error("请完整填写目标用户、标题与内容");
      return;
    }
    try {
      const payload = {
        id: editingMessage?.id,
        user_id: editorForm.user_id,
        type: editorForm.type,
        title: editorForm.title,
        content: editorForm.content,
        priority: Number(editorForm.priority),
      };
      const hide = message.loading("保存中...", 0);
      const res = await add_or_update_message(payload);
      hide();
      if (res.data && res.data.code === 200) {
        message.success(editingMessage ? "更新成功" : "创建成功");
        setShowEditor(false);
        setEditingMessage(null);
        // 刷新列表和未读数
        fetchMessages();
        fetchUnreadCount();
      } else {
        message.error(res.data?.message || "保存失败");
      }
    } catch (err) {
      console.error("保存消息失败:", err);
      message.error(err.response?.data?.message || "保存失败，请稍后重试");
    }
  };

  const renderEditor = () => {
    if (!showEditor) return null;
    return (
      <div className="detail-modal" onClick={() => setShowEditor(false)}>
        <div className="detail-content" onClick={(e) => e.stopPropagation()}>
          <div className="detail-header">
            <h2>{editingMessage ? "编辑消息" : "新建消息"}</h2>
            <button className="close-btn" onClick={() => setShowEditor(false)}>
              ×
            </button>
          </div>
          <div className="detail-body">
            {users.length === 0 ? (
              <div className="error-message">
                仅管理员可创建/编辑消息（未获取到用户列表）。
              </div>
            ) : (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>接收用户</label>
                    <select
                      value={editorForm.user_id}
                      onChange={(e) =>
                        setEditorForm({
                          ...editorForm,
                          user_id: e.target.value,
                        })
                      }
                    >
                      <option value="">请选择用户</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.id} - {u.username || u.email || "未命名用户"}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>类型</label>
                    <select
                      value={editorForm.type}
                      onChange={(e) =>
                        setEditorForm({ ...editorForm, type: e.target.value })
                      }
                    >
                      <option value="system">系统通知</option>
                      <option value="account">账户通知</option>
                      <option value="transaction">交易通知</option>
                      <option value="announcement">公告</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>优先级</label>
                    <select
                      value={editorForm.priority}
                      onChange={(e) =>
                        setEditorForm({
                          ...editorForm,
                          priority: Number(e.target.value),
                        })
                      }
                    >
                      <option value={0}>普通</option>
                      <option value={1}>重要</option>
                      <option value={2}>紧急</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ width: "100%" }}>
                    <label>标题</label>
                    <input
                      type="text"
                      value={editorForm.title}
                      onChange={(e) =>
                        setEditorForm({ ...editorForm, title: e.target.value })
                      }
                      placeholder="请输入标题"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ width: "100%" }}>
                    <label>内容</label>
                    <textarea
                      value={editorForm.content}
                      onChange={(e) =>
                        setEditorForm({
                          ...editorForm,
                          content: e.target.value,
                        })
                      }
                      placeholder="请输入内容"
                      rows={8}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="detail-footer">
            <button
              className="btn-primary"
              onClick={handleSaveMessage}
              disabled={users.length === 0}
            >
              保存
            </button>
            <button
              className="btn-secondary"
              onClick={() => setShowEditor(false)}
            >
              取消
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 查看消息详情
  const handleViewDetail = async (msg) => {
    try {
      const response = await get_message_detail(msg.id);
      if (response.data && response.data.code === 200) {
        setSelectedMessage(response.data.data);
        setShowDetail(true);
        // 刷新列表（因为查看详情会自动标记为已读）
        fetchMessages();
        fetchUnreadCount();
      } else {
        message.error(response.data?.message || "获取消息详情失败");
      }
    } catch (err) {
      console.error("获取消息详情失败:", err);
      message.error("获取消息详情失败，请稍后重试");
    }
  };

  // 标记为已读
  const handleMarkRead = async (msgId, e) => {
    e.stopPropagation();
    try {
      const response = await mark_message_read(msgId);
      if (response.data && response.data.code === 200) {
        message.success("标记为已读成功");
        fetchMessages();
        fetchUnreadCount();
      } else {
        message.error(response.data?.message || "标记失败");
      }
    } catch (err) {
      console.error("标记失败:", err);
      message.error("标记失败，请稍后重试");
    }
  };

  // 标记全部为已读
  const handleMarkAllRead = async () => {
    try {
      const hideLoading = message.loading("正在标记...", 0);
      const response = await mark_all_read();
      hideLoading();
      if (response.data && response.data.code === 200) {
        message.success("全部标记为已读成功");
        fetchMessages();
        fetchUnreadCount();
      } else {
        message.error(response.data?.message || "标记失败");
      }
    } catch (err) {
      console.error("标记失败:", err);
      message.error("标记失败，请稍后重试");
    }
  };

  // 删除消息
  const handleDelete = (msgId, e) => {
    e.stopPropagation();

    Modal.confirm({
      title: "确认删除",
      content: "确定要删除这条消息吗？",
      okText: "删除",
      cancelText: "取消",
      okType: "danger",
      onOk: async () => {
        try {
          const hideLoading = message.loading("正在删除...", 0);
          const response = await delete_message(msgId);
          hideLoading();
          if (response.data && response.data.code === 200) {
            message.success("删除成功");
            fetchMessages();
            fetchUnreadCount();
          } else {
            message.error(response.data?.message || "删除失败");
          }
        } catch (err) {
          console.error("删除失败:", err);
          message.error("删除失败，请稍后重试");
        }
      },
    });
  };

  // 获取类型显示文本
  const getTypeText = (type) => {
    const typeMap = {
      system: "系统通知",
      account: "账户通知",
      transaction: "交易通知",
      announcement: "公告",
    };
    return typeMap[type] || "通知";
  };

  // 获取优先级显示
  const getPriorityBadge = (priority) => {
    if (priority === 2) {
      return <span className="priority-badge urgent">紧急</span>;
    } else if (priority === 1) {
      return <span className="priority-badge important">重要</span>;
    }
    return null;
  };

  // 格式化时间
  const formatTime = (timeStr) => {
    if (!timeStr) return "-";
    const date = new Date(timeStr);
    return date.toLocaleString("zh-CN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // 分页处理
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // 改变每页条数时回到第一页
  };

  return (
    <Layout>
      <div className="message-center">
        <div className="page-header">
          <h1>消息中心</h1>
          <div className="header-actions">
            {users.length > 0 && (
              <button className="btn-primary" onClick={openCreate}>
                新建消息
              </button>
            )}
            {unreadCount > 0 && (
              <button className="btn-secondary" onClick={handleMarkAllRead}>
                全部标记为已读
              </button>
            )}
          </div>
        </div>

        {/* 筛选区域 */}
        <div className="filter-section">
          <div className="filter-row">
            <div className="filter-item">
              <label>消息类型：</label>
              <select
                value={filters.type}
                onChange={(e) =>
                  setFilters({ ...filters, type: e.target.value })
                }
              >
                <option value="all">全部</option>
                <option value="system">系统通知</option>
                <option value="account">账户通知</option>
                <option value="transaction">交易通知</option>
                <option value="announcement">公告</option>
              </select>
            </div>

            <div className="filter-item">
              <label>阅读状态：</label>
              <select
                value={filters.is_read}
                onChange={(e) =>
                  setFilters({ ...filters, is_read: e.target.value })
                }
              >
                <option value="all">全部</option>
                <option value="0">未读</option>
                <option value="1">已读</option>
              </select>
            </div>

            <div className="filter-item">
              <label>搜索标题：</label>
              <input
                type="text"
                placeholder="输入消息标题..."
                value={filters.title}
                onChange={(e) =>
                  setFilters({ ...filters, title: e.target.value })
                }
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    setCurrentPage(1);
                    fetchMessages();
                  }
                }}
              />
            </div>

            <button
              className="btn-secondary"
              onClick={() => {
                setFilters({
                  type: "all",
                  is_read: "all",
                  title: "",
                });
                setCurrentPage(1);
              }}
            >
              重置
            </button>

            <button
              className="btn-primary"
              onClick={() => {
                setCurrentPage(1);
                fetchMessages();
              }}
            >
              查询
            </button>
          </div>
        </div>

        {/* 未读消息提示 */}
        {unreadCount > 0 && (
          <div className="unread-notice">
            您有 <strong>{unreadCount}</strong> 条未读消息
          </div>
        )}

        {/* 消息列表 */}
        <div className="messages-container">
          {loading ? (
            <Loading />
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : messages.length === 0 ? (
            <div className="empty-message">暂无消息</div>
          ) : (
            <div className="messages-list">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`message-item ${
                    msg.is_read === 0 ? "unread" : ""
                  }`}
                  onClick={() => handleViewDetail(msg)}
                >
                  <div className="message-header">
                    <div className="message-title-row">
                      <h3 className="message-title">
                        {msg.is_read === 0 && (
                          <span className="unread-dot"></span>
                        )}
                        {getPriorityBadge(msg.priority)}
                        {msg.title}
                      </h3>
                      <div className="message-meta">
                        <span className="message-type">
                          {msg.type_text || getTypeText(msg.type)}
                        </span>
                        <span className="message-time">
                          {formatTime(msg.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="message-content-preview">
                    {msg.content && msg.content.length > 100
                      ? msg.content.substring(0, 100) + "..."
                      : msg.content}
                  </div>
                  <div className="message-actions">
                    <button
                      className="btn-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetail(msg);
                      }}
                    >
                      查看详情
                    </button>
                    {users.length > 0 && (
                      <button
                        className="btn-link"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(msg);
                        }}
                      >
                        编辑
                      </button>
                    )}

                    <button
                      className="btn-link btn-danger-link"
                      onClick={(e) => handleDelete(msg.id, e)}
                    >
                      删除
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 分页 */}
        {total > 0 && (
          <Pagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalPages={Math.ceil(total / pageSize)}
            totalItems={total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}

        {/* 消息详情弹窗 */}
        {showDetail && selectedMessage && (
          <div className="detail-modal" onClick={() => setShowDetail(false)}>
            <div
              className="detail-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="detail-header">
                <h2>{selectedMessage.title}</h2>
                <button
                  className="close-btn"
                  onClick={() => setShowDetail(false)}
                >
                  ×
                </button>
              </div>
              <div className="detail-body">
                <div className="detail-meta">
                  <span className="detail-type">
                    类型：
                    {selectedMessage.type_text ||
                      getTypeText(selectedMessage.type)}
                  </span>
                  <span className="detail-priority">
                    {getPriorityBadge(selectedMessage.priority)}
                  </span>
                  <span className="detail-time">
                    时间：{formatTime(selectedMessage.created_at)}
                  </span>
                  {selectedMessage.is_read === 1 && (
                    <span className="detail-read-time">
                      已读时间：{formatTime(selectedMessage.read_at)}
                    </span>
                  )}
                  {selectedMessage.username && (
                    <span className="detail-user">
                      接收用户：{selectedMessage.username}
                      {selectedMessage.email && ` (${selectedMessage.email})`}
                    </span>
                  )}
                </div>
                <div className="detail-content-text">
                  {selectedMessage.content}
                </div>
              </div>
              <div className="detail-footer">
                <button
                  className="btn-primary"
                  onClick={() => setShowDetail(false)}
                >
                  关闭
                </button>
                {selectedMessage.is_read === 0 && (
                  <button
                    className="btn-secondary"
                    onClick={async () => {
                      await handleMarkRead(selectedMessage.id, {
                        stopPropagation: () => {},
                      });
                    }}
                  >
                    标记为已读
                  </button>
                )}
                <button
                  className="btn-danger"
                  onClick={() => {
                    Modal.confirm({
                      title: "确认删除",
                      content: "确定要删除这条消息吗？",
                      okText: "删除",
                      cancelText: "取消",
                      okType: "danger",
                      onOk: async () => {
                        await handleDelete(selectedMessage.id, {
                          stopPropagation: () => {},
                        });
                        setShowDetail(false);
                      },
                    });
                  }}
                >
                  删除
                </button>
              </div>
            </div>
          </div>
        )}
        {/* 新建/编辑弹窗 */}
        {renderEditor()}
      </div>
    </Layout>
  );
};

export default MessageCenter;
